import type { ReactNode, RefObject } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import {
  FIELD_TRIGGER_CARET_CLASS,
  FIELD_TRIGGER_EMPTY_CLASS,
  FIELD_TRIGGER_GLYPH_CLASS,
  FieldTrigger,
  fieldPillClass,
  wantedAttr,
} from '../../shared/field_trigger.js';

/*
 * O gatilho do QUANDO do planner de voo e o seu espelho estático, sobre a
 * `FieldTrigger` do intelligence-ui-kit — a mesma pílula do DE/PARA
 * (`PlaceField`), do QUEM (`TravelerTrigger`) e dos campos do hotel e do rent a
 * car. Saiu de `planner_collapsed.tsx` pelo teto de linhas dele.
 */

/** O ESPELHO do QUANDO (o card congelado, o editor da bancada): a mesma pílula
 *  do gatilho, sem hover nem anel de aberto — não abre nada. `fpl-pill` é o
 *  gancho do anel de PENDENTE (`.is-wanted`, CSS do planner). */
export function whenMirrorClass(filled: boolean): string {
  return `fpl-pill fpl-when ${fieldPillClass({ interactive: false })}${
    filled ? '' : ` ${FIELD_TRIGGER_EMPTY_CLASS}`
  }`;
}

/** O gatilho — extraído de `PlannerWhenField` pelo teto de complexidade dele
 *  (a mesma razão de `WhenPopBody`/`portalWhenPop`): o valor, a meia-resposta
 *  pendente e o `data-popup-open` já somam vários ramos sozinhos. */
export function WhenTrigger({
  triggerRef,
  label,
  value,
  pendingTail,
  empty,
  disabled,
  open,
  wanted,
  icon,
  onToggle,
}: Readonly<{
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly label: string;
  readonly value: string;
  readonly pendingTail?: string;
  readonly empty: boolean;
  readonly disabled: boolean;
  readonly open: boolean;
  /** O campo PEDE A VEZ — o anel de pendente do preenchimento guiado da
   *  página binária (`data-wanted`, ver `FIELD_TRIGGER_OPEN_RING_CLASS`); no
   *  multi o anel entra pelo wrapper `.is-wanted` da linha. */
  readonly wanted?: boolean;
  readonly icon?: ReactNode;
  readonly onToggle: () => void;
}>) {
  return (
    <FieldTrigger
      ref={triggerRef}
      // `data-fpl-when-trigger` é o gancho do anel de PENDENTE (`.is-wanted`,
      // CSS do planner) e do foco nos testes; a pele é a `FieldTrigger` das
      // três modais. O caret fecha à direita (a pílula tem piso de largura).
      data-fpl-when-trigger=""
      aria-haspopup="dialog"
      aria-expanded={open}
      // O campo dono da camada aberta se anuncia (`data-popup-open`, #2136):
      // sem a saia de confirmação o popover fecha rápido, e o anel é o que diz
      // de qual campo a camada é. O nome vem do Base UI do desenho; aqui quem
      // o põe é o próprio gatilho, e a `FieldTrigger` já o lê.
      {...(open ? { 'data-popup-open': '' } : {})}
      {...wantedAttr(wanted)}
      // O rótulo da coluna vive fora do botão (a fileira tem rótulo por cima),
      // então o nome acessível junta os dois — sem isso o botão se anuncia só
      // pela data e perde a pergunta que ele responde.
      aria-label={`${label}: ${value}${pendingTail ? ` ${pendingTail}` : ''}`}
      disabled={disabled}
      onClick={onToggle}
    >
      {icon && (
        <span className={FIELD_TRIGGER_GLYPH_CLASS} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={`truncate${empty ? ` ${FIELD_TRIGGER_EMPTY_CLASS}` : ''}`}>
        {value}
        {pendingTail ? <span className="fpl-when-pending"> {pendingTail}</span> : null}
      </span>
      <CaretDownIcon className={FIELD_TRIGGER_CARET_CLASS} aria-hidden />
    </FieldTrigger>
  );
}
